import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Printer, ArrowLeft, Loader2, Download } from 'lucide-react';
import { printLabelsPDF, downloadLabelsPDF } from '../utils/labelPdf';

const API = `https://shopblkmrkt.com/api`;

/**
 * PrintLabels — BLK MRKT thermal labels (4x6 sheet, two 4x3 labels per sheet).
 *
 * Printer-aware: pure white-on-black for B&W thermal printers (no color accents).
 * Single-order edge case: duplicates the label so a 4x6 sheet is never half-blank.
 *
 * URL: /admin/print-labels?ids=BLK-001,BLK-002
 */
export default function PrintLabels() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const ids = (searchParams.get('ids') || '').split(',').filter(Boolean);

  useEffect(() => {
    document.title = `BLK MRKT LABELS — ${ids.length}`;
  }, [ids.length]);

  useEffect(() => {
    if (ids.length === 0) {
      setError('No order IDs provided.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/orders`, { headers: getAuthHeader() });
        const all = Array.isArray(res.data) ? res.data : res.data.orders || [];
        const filtered = all.filter(o => ids.includes(o.id));
        filtered.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
        setOrders(filtered);
      } catch (e) {
        setError('Failed to load orders');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const getShippingLabel = (order) => {
    const method = (order.shipping_method || '').toLowerCase().trim();
    if (method) {
      // Admin explicitly set the shipping method — honor it.
      if (method.includes('express') || method.includes('overnight')) return 'OVERNIGHT';
      if (method.includes('priority')) return 'PRIORITY';
      return 'GROUND';
    }
    // No admin override — fall back to the shipping boolean flags from checkout.
    if (order.overnight_shipping) return 'OVERNIGHT';
    return order.priority_shipping ? 'PRIORITY' : 'GROUND';
  };

  const getCustomerName = (order) => {
    const shipping = order.shipping_address || {};
    return (shipping.full_name || order.user_name || order.user_email || 'CUSTOMER').toUpperCase();
  };

  const handlePrint = () => printLabelsPDF(orders);
  const handleDownload = () => downloadLabelsPDF(orders, `blkmrkt-labels-${ids.length}.pdf`);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-tiffany-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-8 text-center">
        <div>
          <p className="font-mono text-red-500 mb-4">{error}</p>
          <button onClick={() => navigate(-1)} className="font-mono text-xs text-zinc-400 hover:text-white">← BACK</button>
        </div>
      </div>
    );
  }

  // Build sheets of 2 labels. If the last sheet has only 1 label, duplicate it
  // so a 4x6 sheet is never half-blank (per user request when N is odd).
  const sheets = [];
  for (let i = 0; i < orders.length; i += 2) {
    const pair = orders.slice(i, i + 2);
    if (pair.length === 1) pair.push(pair[0]); // duplicate to fill bottom half
    sheets.push(pair);
  }

  const renderLabel = (order, idx) => {
    const shipping = getShippingLabel(order);
    const items = order.items || [];

    return (
      <div
        key={`${order.id}-${idx}`}
        style={{
          background: '#000',
          color: '#fff',
          fontFamily: "'Courier New', Courier, monospace",
          padding: '0.18in',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          breakInside: 'avoid',
          pageBreakInside: 'avoid',
          overflow: 'hidden',
        }}
      >
        {/* Top bar — BLK MRKT logo + shipping method (B&W safe) */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid #fff',
          paddingBottom: '6px',
          marginBottom: '8px',
          flexShrink: 0,
        }}>
          <div style={{ color: '#fff', fontSize: '20px', fontWeight: 900, letterSpacing: '3px' }}>
            BLK MRKT
          </div>
          <div style={{
            background: '#fff',
            color: '#000',
            fontSize: '13px',
            fontWeight: 900,
            letterSpacing: '2px',
            padding: '3px 9px',
          }}>
            {shipping}
          </div>
        </div>

        {/* Customer name */}
        <div style={{
          fontSize: '20px',
          fontWeight: 900,
          color: '#fff',
          letterSpacing: '1px',
          lineHeight: 1.05,
          marginBottom: '6px',
          wordBreak: 'break-word',
          flexShrink: 0,
        }}>
          {getCustomerName(order)}
        </div>

        {/* Items list — fills available middle space */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          fontSize: '12px',
          lineHeight: 1.45,
        }}>
          {items.length === 0 ? (
            <div style={{ color: '#bbb', fontStyle: 'italic' }}>(no items)</div>
          ) : (
            items.map((it, i) => {
              const qty = it.quantity || 1;
              const variant = it.variant_name ? ` — ${it.variant_name.toUpperCase()}` : '';
              const isTrade = it.is_trade;
              const isOffer = it.offer_price !== undefined && it.offer_price !== null;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '6px',
                    marginBottom: '3px',
                  }}
                >
                  <span style={{ color: '#fff', fontWeight: 900, flexShrink: 0, minWidth: '24px' }}>
                    {qty}×
                  </span>
                  <span style={{ color: '#fff', flex: 1, wordBreak: 'break-word' }}>
                    {(it.name || it.product_name || '').toUpperCase()}{variant}
                    {isTrade && (
                      <span style={{
                        background: '#fff', color: '#000', marginLeft: '4px',
                        fontSize: '9px', fontWeight: 900, padding: '1px 4px',
                      }}>TRADE</span>
                    )}
                    {isOffer && !isTrade && (
                      <span style={{
                        background: '#fff', color: '#000', marginLeft: '4px',
                        fontSize: '9px', fontWeight: 900, padding: '1px 4px',
                      }}>OFFER</span>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Weight — hand-write blank line above the Order ID separator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginTop: '4px',
          flexShrink: 0,
          color: '#fff',
          fontSize: '10px',
          fontWeight: 900,
          letterSpacing: '1px',
        }}>
          <span>WT:</span>
          <span style={{ width: '1in', borderBottom: '1.5px solid #fff', height: '14px' }} />
          <span>OZ</span>
        </div>

        {/* Bottom: Order ID — readable from across the room */}
        <div style={{
          borderTop: '2px solid #fff',
          paddingTop: '6px',
          marginTop: '6px',
          textAlign: 'center',
          flexShrink: 0,
        }}>
          <div style={{
            color: '#fff',
            fontSize: '32px',
            fontWeight: 900,
            letterSpacing: '2px',
            lineHeight: 1,
          }}>
            {order.id}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @page { size: 4in 6in; margin: 0; }

        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important; }
          .print-wrapper {
            padding: 0 !important;
            margin: 0 !important;
            background: #fff !important;
            min-height: 0 !important;
          }
          .sheet {
            width: 4in !important;
            height: 6in !important;
            margin: 0 !important;
            box-shadow: none !important;
            page-break-after: always;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .sheet:last-child { page-break-after: auto; }
        }

        /* On-screen preview only */
        .sheet {
          width: 4in;
          height: 6in;
          background: #fff;
          box-shadow: 0 2px 12px rgba(0,0,0,0.3);
          margin: 0 auto 20px;
          box-sizing: border-box;
          display: grid;
          grid-template-rows: 3in 3in;
          gap: 0;
          overflow: hidden;
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-black border-b border-zinc-800">
        <div className="p-3 flex items-center justify-between gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white font-mono text-xs"
            data-testid="print-labels-back"
          >
            <ArrowLeft className="w-4 h-4" /> BACK
          </button>
          <span className="font-mono text-xs text-zinc-500 hidden sm:inline">
            {orders.length} ORDER{orders.length === 1 ? '' : 'S'} · 4×3 LABELS · {sheets.length} SHEET{sheets.length === 1 ? '' : 'S'} · B&W
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 bg-zinc-800 text-white font-mono text-xs font-bold px-3 py-2 hover:bg-zinc-700"
              data-testid="print-labels-download"
            >
              <Download className="w-4 h-4" /> PDF
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-tiffany-500 text-black font-mono text-xs font-bold px-4 py-2 hover:bg-tiffany-400"
              data-testid="print-labels-print"
            >
              <Printer className="w-4 h-4" /> PRINT
            </button>
          </div>
        </div>
        <div className="px-3 pb-2 font-mono text-[10px] text-zinc-500 text-center" data-testid="print-labels-tip">
          PRINT DIALOG — SET <span className="text-white">PAPER = 4×6</span> · <span className="text-white">SCALE = 100% / ACTUAL SIZE</span>
        </div>
      </div>

      <div className="print-wrapper bg-zinc-950 min-h-screen py-6 px-2">
        {sheets.map((sheetOrders, sheetIdx) => (
          <div key={sheetIdx} className="sheet">
            {sheetOrders.map((order, i) => renderLabel(order, sheetIdx * 2 + i))}
          </div>
        ))}
      </div>
    </>
  );
}
